/**
 * Where the AVD lifecycle lives on GitHub, plus the two helpers that talk to it.
 *
 * Lives in this shared package because two callers need to agree on these: the scaffolder actions
 * that DISPATCH a run, and the registry reconciler in `plugins/avd-backend` that later READS that
 * run to settle a desktop's status. If they disagreed about the owner, repo, branch or API base the
 * reconciler would quietly look up runs that do not exist and every in-flight desktop would sit at
 * `provisioning` forever — a silent failure, which is exactly the drift class this package exists
 * to remove.
 *
 * Note on the frontend: `packages/app` imports this package for the entitlement gates, so this
 * module rides along in the app bundle. It is ~50 lines and pulls in no dependency beyond `fetch`,
 * which the browser has natively — the cost is negligible, and it is a deliberate trade for making
 * the dispatcher/reconciler agreement structural.
 */

/**
 * The GHE WEB origin — `imcg.ghe.com`, never public github.com. Split from the API base below so the
 * two cannot drift: the admin page links a human at `<web>/owner/repo/actions/workflows/...`
 * while the reconciler calls `<web>/api/v3/...`, and a wrong host silently answers with a
 * right-looking page via the pre-migration rename redirect (see CLAUDE.md § Repos & hosting).
 */
export const GITHUB_WEB_BASE = "https://imcg.ghe.com";
export const GITHUB_API_BASE = `${GITHUB_WEB_BASE}/api/v3`;
export const INFRA_REPO_OWNER = "thetechcollective";
export const INFRA_REPO = "backstage-im-dev";
export const INFRA_BRANCH = "main";

/** Derives a filesystem/state-safe slug from a UPN (e.g. `asser@thetechcollective.eu` → `asser-thetechcollective-eu`). */
export function toSlug(upn: string): string {
	return upn
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");
}

/** Thin wrapper around fetch for the GHE REST API with standard headers. */
export async function githubRequest(
	token: string,
	method: string,
	path: string,
	body?: unknown,
): Promise<Response> {
	return fetch(`${GITHUB_API_BASE}${path}`, {
		method,
		headers: {
			Authorization: `Bearer ${token}`,
			Accept: "application/vnd.github+json",
			"X-GitHub-Api-Version": "2022-11-28",
			"Content-Type": "application/json",
		},
		body: body ? JSON.stringify(body) : undefined,
	});
}

/**
 * Polls up to 60s for a run dispatched at/after `dispatchedAt` whose `display_title` contains `marker`;
 * why both halves are needed: docs/app/testing.md § contract tests (`findDispatchedRun`).
 */
export async function findDispatchedRun(
	token: string,
	workflowFile: string,
	dispatchedAt: number,
	marker: string,
): Promise<{ id: number; url: string } | undefined> {
	for (let attempt = 0; attempt < 12; attempt++) {
		await new Promise((r) => setTimeout(r, 5000));
		const res = await githubRequest(
			token,
			"GET",
			`/repos/${INFRA_REPO_OWNER}/${INFRA_REPO}/actions/workflows/${workflowFile}/runs?event=workflow_dispatch&per_page=10`,
		);
		if (res.ok) {
			const data = (await res.json()) as {
				workflow_runs: Array<{
					id: number;
					html_url: string;
					created_at: string;
					display_title: string;
				}>;
			};
			const run = data.workflow_runs?.find(
				(r) =>
					new Date(r.created_at).getTime() >= dispatchedAt - 10000 &&
					r.display_title?.includes(marker),
			);
			if (run) {
				return { id: run.id, url: run.html_url };
			}
		}
	}
	return undefined;
}

/**
 * The lifecycle state of one Actions run, as the reconciler needs it.
 *
 * `status` is GitHub's own vocabulary (`queued` / `in_progress` / `completed` / …) and `conclusion`
 * is only meaningful once `status === "completed"`.
 */
export type WorkflowRunState = {
	status: string;
	conclusion: string | null;
	html_url: string;
};

/**
 * Reads one run by id. Returns `undefined` when the run cannot be read — 404 (the run was deleted
 * or log retention expired), 403, or any transport error.
 *
 * `undefined` MUST NOT be treated as failure by the caller: it is "no new information". The
 * reconciler's whole job is to settle desktops that a missed callback left in flight, and turning
 * an unreadable run into `failed` would mark a perfectly healthy desktop as broken.
 */
export async function getWorkflowRun(
	token: string,
	runId: number,
): Promise<WorkflowRunState | undefined> {
	try {
		const res = await githubRequest(
			token,
			"GET",
			`/repos/${INFRA_REPO_OWNER}/${INFRA_REPO}/actions/runs/${runId}`,
		);
		if (!res.ok) return undefined;
		const run = (await res.json()) as Partial<WorkflowRunState>;
		if (typeof run?.status !== "string") return undefined;
		return {
			status: run.status,
			conclusion: run.conclusion ?? null,
			html_url: run.html_url ?? "",
		};
	} catch {
		return undefined;
	}
}
